import Autocomplete from "@mui/material/Autocomplete"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import { axiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"

export const CUSTOMER_ACCESS_STATUSES = ["Active", "Inactive"]

export const INITIAL_CUSTOMER_ACCESS_FORM = {
  email: "",
  first_name: "",
  last_name: "",
  job_title: "",
  city: "",
  state: "",
  phone: "",
  status: "Active",
  permission_level: "Member",
  campaigns: [],
}

const TEXT_FIELDS = [
  { name: "email", label: "User ID (Email)", type: "email" },
  { name: "first_name", label: "First" },
  { name: "last_name", label: "Last" },
  { name: "job_title", label: "Job Title" },
  { name: "city", label: "City" },
  { name: "state", label: "State" },
  { name: "phone", label: "Phone", type: "tel" },
]

export function toCustomerAccessPayload(form) {
  return {
    email: form.email.toLowerCase().trim(),
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    job_title: form.job_title.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    phone: form.phone.trim(),
    status: form.status,
    permission_level: form.permission_level,
    campaigns: form.campaigns.map((campaign) => campaign.id),
  }
}

export function formatCustomerAccessError(err, fallback) {
  const data = err?.response?.data
  if (err?.response?.status === 429) {
    return "Too many submissions. Please wait a while and try again."
  }
  if (typeof data === "string") return data
  if (data && typeof data === "object") {
    const messages = Object.entries(data).flatMap(([field, value]) => {
      const items = Array.isArray(value) ? value : [value]
      return items.map((item) =>
        field === "non_field_errors" || field === "detail"
          ? String(item)
          : `${field}: ${item}`
      )
    })
    return messages.join(" ") || fallback
  }
  return fallback
}

export function useCustomerAccessCampaigns() {
  const [campaigns, setCampaigns] = useState([])

  useEffect(() => {
    let cancelled = false
    axiosClient
      .get("/users/api/customer-access/campaigns/")
      .then((res) => {
        if (!cancelled) setCampaigns(res.data)
      })
      .catch(() => {
        if (!cancelled) setCampaigns([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  return campaigns
}

export default function CustomerAccessFields({ form, setForm, campaigns }) {
  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <Column gap={2}>
      {TEXT_FIELDS.map(({ name, label, type }) => (
        <TextField
          key={name}
          name={name}
          label={label}
          type={type || "text"}
          value={form[name]}
          onChange={(e) => setField(name, e.target.value)}
          size="small"
          fullWidth
          required
          autoComplete="off"
        />
      ))}
      <TextField
        select
        label="Status"
        value={form.status}
        onChange={(e) => setField("status", e.target.value)}
        size="small"
        fullWidth
        required
      >
        {CUSTOMER_ACCESS_STATUSES.map((status) => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Permission Level"
        value={form.permission_level}
        size="small"
        fullWidth
        disabled
      />
      <Autocomplete
        multiple
        options={campaigns}
        value={form.campaigns}
        onChange={(_, value) => setField("campaigns", value)}
        getOptionLabel={(campaign) => campaign.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField {...params} label="Campaigns" size="small" />
        )}
      />
    </Column>
  )
}

CustomerAccessFields.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ).isRequired,
}
